import type React from 'react'
import type { JSX } from 'react'
import type { IFallbackImageProps } from 'atoms/FallbackImage'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { ITextBlockProps } from 'blocks/TextBlock'

import { memo } from 'react'
import { Icon, IconNames, IconSizes } from 'atoms/Icon'
import { ImagePoster } from 'atoms/ImagePoster'
import { TextBoxMaxLine1Footnote4 } from 'atoms/TextBox/Templates'
import {
  ContainerWithImageSC,
  LogoSC,
  TextBlock,
  TextBlockAlignItems,
  TextBlockDirection,
  TextBlockJustifyContent,
  TextBlockRowGap,
} from 'blocks/TextBlock'
import { COLOR } from 'vars/index'

export interface ITextBlockWithLogo extends ITextBlockProps {
  children?:
    | React.ReactElement<ITextBoxProps>
    | React.ReactElement<ITextBoxProps>[]
  title: ITextBoxProps['title']
  posterUrl: string
  backgroundLogo?: COLOR
  roundedLogo?: boolean
  justifyContent?: TextBlockJustifyContent
  alignItems?: TextBlockAlignItems
  rowGap?: ITextBlockProps['rowGap']
  heightLogo?: number
  widthLogo?: number
  fallbackImageSize?: IFallbackImageProps['size']
  fallbackImageBackgroundColor?: IFallbackImageProps['background']
  fallbackImageColor?: IFallbackImageProps['color']
  locked?: boolean
  lockedColor?: COLOR
  lockedSizeIcon?: IconSizes
}

export const TextBlockWithLogo = memo(
  ({
    direction = TextBlockDirection.ROW,
    children: _unused,
    title,
    posterUrl,
    backgroundLogo = COLOR.SURFACE_OPACITY_LIGHT_10,
    roundedLogo = true,
    justifyContent = TextBlockJustifyContent.FlexStart,
    alignItems = TextBlockAlignItems.Center,
    rowGap = TextBlockRowGap.INDENT_40,
    heightLogo = 56,
    widthLogo = 56,
    fallbackImageSize,
    fallbackImageColor,
    fallbackImageBackgroundColor,
    locked,
    lockedColor = COLOR.TEXT_ICON_PRIMARY,
    lockedSizeIcon = IconSizes.large,
    ...rest
  }: ITextBlockWithLogo): JSX.Element => {
    const Poster = () =>
      locked ? (
        <Icon
          name={IconNames.pinLock}
          size={lockedSizeIcon}
          color={lockedColor}
        />
      ) : (
        <ImagePoster
          posterUrl={posterUrl}
          fallbackImageColor={fallbackImageColor}
          fallbackImageSize={fallbackImageSize}
        />
      )

    return (
      <TextBlock {...rest}>
        <ContainerWithImageSC
          justifyContent={justifyContent}
          alignItems={alignItems}
          rowGap={rowGap}
          direction={direction}
          rounded={roundedLogo}
          backgroundLogo={backgroundLogo}
          posterUrl={posterUrl}
          height={heightLogo}
          width={widthLogo}
          fallbackImageBackgroundColor={fallbackImageBackgroundColor}
        >
          <LogoSC>
            <Poster />
          </LogoSC>
          <TextBoxMaxLine1Footnote4 title={title} />
        </ContainerWithImageSC>
      </TextBlock>
    )
  }
)
